import {
  ANALYTICS_VERSION,
  MAX_BATCH_BYTES,
  byteLength,
  cleanPath,
  newEventId,
  validId,
  validProps,
  type AnalyticsEvent,
  type AnalyticsEventName,
  type AnalyticsSource,
  type EventProps,
} from './contract';

const VISIT_MS = 30 * 60 * 1000;
interface Visit {
  id: string;
  lastAt: number;
  landingPath: string;
  referrer?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  journeyId?: string;
}
interface Queued {
  event: AnalyticsEvent;
  identity: string | null;
  attempts: number;
}
export interface AnalyticsOptions {
  endpoint: string;
  source: 'jamcam-web' | 'jamcam-app';
  authEndpoint?: string;
}

/** Bounded, non-blocking delivery. Tokens stay in memory and are never persisted. */
export function createAnalyticsClient() {
  let options: AnalyticsOptions | undefined;
  let anonId = '';
  let visit: Visit | undefined;
  let identity: string | null = null;
  let tokenProvider: (() => Promise<string | null>) | null = null;
  let queue: Queued[] = [];
  let early: Array<[AnalyticsEventName, EventProps]> = [];
  let timer: ReturnType<typeof setInterval> | undefined;
  let inflight = false;
  let lastPage = '';
  let lastPageAt = 0;
  const seen = new Set<string>();
  const health = {
    accepted: 0,
    delivered: 0,
    rejected: 0,
    retried: 0,
    dropped: 0,
  };
  const storageGet = (
    store: 'localStorage' | 'sessionStorage',
    key: string
  ) => {
    try {
      return window[store].getItem(key);
    } catch {
      return null;
    }
  };
  const storageSet = (
    store: 'localStorage' | 'sessionStorage',
    key: string,
    value: string
  ) => {
    try {
      window[store].setItem(key, value);
    } catch {
      /* Optional browser storage. */
    }
  };

  function currentVisit(): Visit {
    const now = Date.now();
    if (!visit || now - visit.lastAt > VISIT_MS) {
      const params = new URLSearchParams(window.location.search);
      let referrer: string | undefined;
      try {
        referrer = document.referrer
          ? new URL(document.referrer).origin
          : undefined;
      } catch {
        /* malformed referrer */
      }
      visit = {
        id: newEventId(),
        lastAt: now,
        landingPath: cleanPath(window.location.href),
        ...(referrer ? { referrer } : {}),
      };
      for (const [query, field] of [
        ['utm_source', 'utmSource'],
        ['utm_medium', 'utmMedium'],
        ['utm_campaign', 'utmCampaign'],
        ['utm_term', 'utmTerm'],
        ['utm_content', 'utmContent'],
      ] as const) {
        const value = params.get(query)?.trim().slice(0, 128);
        if (value) visit[field] = value;
      }
      const journey = params.get('jc_journey');
      const journeyAt = Number(params.get('jc_at'));
      if (validId(journey) && journeyAt <= now && now - journeyAt < VISIT_MS)
        visit.journeyId = journey;
      seen.clear();
    }
    visit.lastAt = now;
    storageSet('sessionStorage', 'jc_visit_v2', JSON.stringify(visit));
    return visit;
  }

  function track(event: AnalyticsEventName, props: EventProps = {}) {
    if (typeof window === 'undefined') return;
    if (!validProps(event, props) || byteLength(JSON.stringify(props)) > 4096) {
      health.rejected++;
      return;
    }
    if (!options) {
      if (early.length < 50) early.push([event, props]);
      return;
    }
    const current = currentVisit();
    const path = cleanPath(window.location.href);
    if (
      event === 'page_view' &&
      lastPage === path &&
      Date.now() - lastPageAt < 1000
    )
      return;
    if (event === 'page_view') {
      lastPage = path;
      lastPageAt = Date.now();
    }
    const { id: sessionId, lastAt: _lastAt, ...attribution } = current;
    const source: AnalyticsSource =
      identity && options.authEndpoint ? 'jamcam-app-auth' : options.source;
    queue.push({
      identity: source === 'jamcam-app-auth' ? identity : null,
      attempts: 0,
      event: {
        schemaVersion: ANALYTICS_VERSION,
        eventId: newEventId(),
        event,
        source,
        sessionId,
        anonId,
        ts: new Date().toISOString(),
        path,
        ...attribution,
        props: { ...props },
      },
    });
    health.accepted++;
    if (queue.length > 200) {
      queue.shift();
      health.dropped++;
    }
    if (queue.length >= 25) void flush();
  }

  async function flush() {
    if (!options || inflight || !queue.length) return;
    inflight = true;
    try {
      const authIdentity = queue[0].identity;
      const batch: Queued[] = [];
      while (
        queue.length &&
        batch.length < 25 &&
        queue[0].identity === authIdentity
      ) {
        const next = queue[0];
        if (
          byteLength(
            JSON.stringify({
              events: [...batch.map((item) => item.event), next.event],
            })
          ) > MAX_BATCH_BYTES
        )
          break;
        batch.push(queue.shift()!);
      }
      if (!batch.length) {
        queue.shift();
        health.dropped++;
        return;
      }
      let token: string | null = null;
      if (authIdentity) {
        if (identity !== authIdentity || !tokenProvider) {
          health.dropped += batch.length;
          return;
        }
        try {
          token = await tokenProvider();
        } catch {
          /* no valid account token */
        }
        // Never stamp an old account's buffered events with a newly signed-in account.
        if (!token || identity !== authIdentity) {
          health.dropped += batch.length;
          return;
        }
      }
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(
          authIdentity ? options.authEndpoint! : options.endpoint,
          {
            method: 'POST',
            credentials: 'omit',
            mode: 'cors',
            keepalive: true,
            signal: controller.signal,
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ events: batch.map((item) => item.event) }),
          }
        );
        if (response.ok) {
          let rejected = 0;
          try {
            const result = await response.json();
            rejected =
              typeof result.rejected === 'number'
                ? Math.min(batch.length, Math.max(0, result.rejected))
                : 0;
          } catch {
            /* Legacy 204 responses have no body. */
          }
          health.delivered += batch.length - rejected;
          health.rejected += rejected;
          return;
        }
        if (response.status !== 429 && response.status < 500) {
          health.rejected += batch.length;
          return;
        }
      } catch {
        /* Retry transient transport failures on the next timer tick. */
      } finally {
        clearTimeout(timeout);
      }
      for (const item of batch) {
        if (++item.attempts < 3 && queue.length < 200) {
          queue.push(item);
          health.retried++;
        } else health.dropped++;
      }
    } finally {
      inflight = false;
    }
  }
  const onHidden = () => {
    if (document.visibilityState === 'hidden') void flush();
  };
  const onPageHide = () => {
    void flush();
  };

  function init(next: AnalyticsOptions) {
    if (options || typeof window === 'undefined') return;
    options = next;
    const stored = storageGet('localStorage', 'jc_anon_id');
    anonId = validId(stored) ? stored : newEventId();
    storageSet('localStorage', 'jc_anon_id', anonId);
    try {
      const saved = JSON.parse(
        storageGet('sessionStorage', 'jc_visit_v2') ?? 'null'
      );
      if (
        saved &&
        validId(saved.id) &&
        typeof saved.lastAt === 'number' &&
        typeof saved.landingPath === 'string'
      )
        visit = saved;
    } catch {
      /* A corrupt visit starts fresh. */
    }
    const incoming = new URLSearchParams(window.location.search);
    const journey = incoming.get('jc_journey');
    const age = Date.now() - Number(incoming.get('jc_at'));
    if (
      visit &&
      validId(journey) &&
      age >= 0 &&
      age < VISIT_MS &&
      journey !== visit.journeyId
    )
      visit = undefined;
    currentVisit();
    timer = setInterval(() => {
      void flush();
    }, 5000);
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onPageHide);
    const pending = early;
    early = [];
    pending.forEach(([event, props]) => track(event, props));
  }
  function setAuth(
    provider: (() => Promise<string | null>) | null,
    accountId: string | null = null
  ) {
    identity = provider ? accountId : null;
    tokenProvider = provider;
  }
  function trackOnce(
    key: string,
    event: AnalyticsEventName,
    props: EventProps = {}
  ) {
    if (typeof window === 'undefined') return;
    const visitId = options ? currentVisit().id : 'initial';
    const marker = `jc_seen_v2:${visitId}:${key}`;
    if (seen.has(marker) || storageGet('sessionStorage', marker)) return;
    seen.add(marker);
    storageSet('sessionStorage', marker, '1');
    track(event, props);
  }
  /** Add an opaque journey token and campaign attribution only to a configured app destination. */
  function destination(href: string, allowedOrigin: string): string {
    if (typeof window === 'undefined') return href;
    const url = new URL(href, window.location.href);
    if (url.origin !== new URL(allowedOrigin).origin) return href;
    const current = currentVisit();
    current.journeyId ??= newEventId();
    storageSet('sessionStorage', 'jc_visit_v2', JSON.stringify(current));
    url.searchParams.set('jc_journey', current.journeyId);
    url.searchParams.set('jc_at', String(Date.now()));
    for (const [query, field] of [
      ['utm_source', 'utmSource'],
      ['utm_medium', 'utmMedium'],
      ['utm_campaign', 'utmCampaign'],
      ['utm_term', 'utmTerm'],
      ['utm_content', 'utmContent'],
    ] as const) {
      if (current[field]) url.searchParams.set(query, current[field]!);
    }
    return url.toString();
  }
  function reset() {
    if (timer) clearInterval(timer);
    if (typeof window !== 'undefined') {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onPageHide);
    }
    options = undefined;
    visit = undefined;
    queue = [];
    early = [];
    identity = null;
    tokenProvider = null;
    seen.clear();
    lastPage = '';
    lastPageAt = 0;
  }
  return { init, track, trackOnce, flush, setAuth, destination, reset, health };
}
